import React, { useState, useMemo, useEffect, useRef } from "react";
import {
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalTitle,
} from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { Loader2, GitBranch, ArrowRight, Info, ChevronDown, Check } from "lucide-react";

const toUiVersionNumber = (version) => {
    const numericVersion = Number(version);
    return Number.isFinite(numericVersion) ? numericVersion + 1 : 1;
};

export function CreateVersionModal({
    open,
    onOpenChange,
    onConfirm,
    versions = [],
    currentVersion,
    currentRecipeId,
    className,
    isLoading = false,
}) {
    const [error, setError] = useState(null);
    const [selectedBaseRecipeId, setSelectedBaseRecipeId] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Sort versions descending (newest / highest stored version first)
    const sortedVersions = useMemo(() => {
        if (!Array.isArray(versions) || versions.length === 0) return [];
        return [...versions].sort((a, b) => (Number(b.version) || 0) - (Number(a.version) || 0));
    }, [versions]);

    const maxStoredVersion = useMemo(() => {
        if (!sortedVersions.length) return typeof currentVersion === "number" ? currentVersion : 0;
        return Math.max(...sortedVersions.map((v) => Number(v.version) || 0));
    }, [sortedVersions, currentVersion]);

    const nextUiVersionNumber = toUiVersionNumber(maxStoredVersion + 1);

    // Build selectable options
    const versionOptions = useMemo(() => {
        if (!sortedVersions.length) {
            if (currentRecipeId) {
                const strId = String(
                    typeof currentRecipeId === "object" && currentRecipeId !== null
                        ? currentRecipeId._id || currentRecipeId.id || ""
                        : currentRecipeId
                );
                const uiNum = toUiVersionNumber(currentVersion ?? 0);
                return [
                    {
                        value: strId,
                        label: `Version ${uiNum} (Current View)`,
                        uiVersion: uiNum,
                        version: currentVersion ?? 0,
                        recipeCode: "Current",
                        isCurrent: true,
                        isLatest: true,
                    },
                ];
            }
            return [];
        }

        return sortedVersions.map((v) => {
            const vNum = v.version ?? 0;
            const uiNum = toUiVersionNumber(vNum);
            const strId = String(v._id || "");
            const currentStrId = String(
                typeof currentRecipeId === "object" && currentRecipeId !== null
                    ? currentRecipeId._id || currentRecipeId.id || ""
                    : currentRecipeId || ""
            );
            const isCurrent = (strId && strId === currentStrId) || (vNum === currentVersion);
            const isLatest = vNum === maxStoredVersion;
            const code = v.recipeCode || v.inDevelopmentCode || v.referenceCode || "Draft";
            
            return {
                value: strId,
                label: `Version ${uiNum} (${code})`,
                uiVersion: uiNum,
                version: vNum,
                recipeCode: code,
                status: v.recipeStatus,
                isCurrent,
                isLatest,
            };
        });
    }, [sortedVersions, currentRecipeId, currentVersion, maxStoredVersion]);

    // Set initial selected version when modal opens
    useEffect(() => {
        if (open) {
            setError(null);
            setIsDropdownOpen(false);
            const currentStrId = String(
                typeof currentRecipeId === "object" && currentRecipeId !== null
                    ? currentRecipeId._id || currentRecipeId.id || ""
                    : currentRecipeId || ""
            );
            if (currentStrId && versionOptions.some((opt) => opt.value === currentStrId)) {
                setSelectedBaseRecipeId(currentStrId);
            } else if (versionOptions.length > 0) {
                setSelectedBaseRecipeId(versionOptions[0].value);
            }
        }
    }, [open, currentRecipeId, versionOptions]);

    // Handle click outside dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        if (isDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isDropdownOpen]);

    const selectedOption = useMemo(() => {
        return versionOptions.find((opt) => opt.value === selectedBaseRecipeId) || versionOptions[0] || null;
    }, [versionOptions, selectedBaseRecipeId]);

    const handleClose = (isOpen) => {
        if (!isLoading) {
            onOpenChange(isOpen);
            setError(null);
            setIsDropdownOpen(false);
        }
    };

    const handleConfirm = async () => {
        setError(null);
        try {
            const targetId = selectedBaseRecipeId || (
                typeof currentRecipeId === "object" && currentRecipeId !== null
                    ? currentRecipeId._id || currentRecipeId.id
                    : currentRecipeId
            );
            await onConfirm(String(targetId));
            handleClose(false);
        } catch (err) {
            setError(err?.message || "An error occurred while creating a new version");
        }
    };

    return (
        <Modal open={open} onOpenChange={handleClose}>
            <ModalContent
                className={cn(
                    "max-w-[500px] p-6 sm:p-8 rounded-[28px] bg-white dark:bg-[#121019] shadow-2xl border border-border/50",
                    className
                )}
            >
                <ModalHeader className="p-0 pb-3 text-left">
                    <div className="flex items-center gap-2.5 text-primary mb-1">
                        <div className="p-2 rounded-xl bg-primary/10 text-primary">
                            <GitBranch className="w-5 h-5" />
                        </div>
                        <ModalTitle className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                            Create New Version
                        </ModalTitle>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Create an incremental recipe version snapshot from an existing version.
                    </p>
                </ModalHeader>

                <div className="flex flex-col gap-5 text-left py-2">
                    {/* Base Version Selector */}
                    <div className="space-y-2 relative" ref={dropdownRef}>
                        <label className="text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                            Select Base Version <span className="text-red-500">*</span>
                        </label>
                        
                        <button
                            type="button"
                            onClick={() => !isLoading && setIsDropdownOpen((prev) => !prev)}
                            disabled={isLoading}
                            className={cn(
                                "w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all text-sm font-medium",
                                "bg-gray-50 dark:bg-gray-900/60 border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white",
                                "hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20",
                                isDropdownOpen && "border-primary ring-2 ring-primary/20"
                            )}
                        >
                            <div className="flex items-center gap-2.5 truncate mr-2">
                                <span className="font-semibold text-primary dark:text-purple-300">
                                    Version {selectedOption?.uiVersion ?? toUiVersionNumber(currentVersion ?? 0)}
                                </span>
                                <span className="text-gray-400">|</span>
                                <span className="text-gray-600 dark:text-gray-300 truncate">
                                    {selectedOption?.recipeCode || "Draft"}
                                </span>
                                {selectedOption?.isCurrent && (
                                    <span className="text-[11px] px-2 py-0.5 rounded-md bg-primary/10 text-primary font-medium">
                                        Current View
                                    </span>
                                )}
                                {selectedOption?.isLatest && !selectedOption?.isCurrent && (
                                    <span className="text-[11px] px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-medium">
                                        Latest
                                    </span>
                                )}
                            </div>
                            <ChevronDown
                                className={cn(
                                    "w-4 h-4 text-gray-400 transition-transform duration-200 shrink-0",
                                    isDropdownOpen && "rotate-180 text-primary"
                                )}
                            />
                        </button>

                        {/* Dropdown Menu */}
                        {isDropdownOpen && (
                            <div className="absolute z-50 left-0 right-0 top-full mt-1.5 p-1.5 rounded-xl bg-white dark:bg-[#1A1625] border border-gray-200 dark:border-gray-800 shadow-xl max-h-56 overflow-y-auto space-y-1">
                                {versionOptions.map((opt) => {
                                    const isSelected = opt.value === selectedOption?.value;
                                    return (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => {
                                                setSelectedBaseRecipeId(opt.value);
                                                setIsDropdownOpen(false);
                                            }}
                                            className={cn(
                                                "w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left text-sm transition-colors",
                                                isSelected
                                                    ? "bg-primary/10 text-primary font-semibold"
                                                    : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/60"
                                            )}
                                        >
                                            <div className="flex items-center gap-2 truncate mr-2">
                                                <span className={cn(
                                                    "px-2 py-0.5 rounded text-xs font-bold",
                                                    isSelected ? "bg-primary text-white" : "bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                                                )}>
                                                    v{opt.uiVersion}
                                                </span>
                                                <span className="truncate">{opt.recipeCode || "Draft"}</span>
                                                {opt.isCurrent && (
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300 font-medium">
                                                        Current View
                                                    </span>
                                                )}
                                                {opt.isLatest && (
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 font-medium">
                                                        Latest
                                                    </span>
                                                )}
                                            </div>
                                            {isSelected && <Check className="w-4 h-4 text-primary shrink-0" />}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Version Transition Overview */}
                    <div className="p-4 rounded-2xl bg-primary/5 dark:bg-primary/10 border border-primary/20 space-y-3">
                        <div className="flex items-center justify-between gap-3 text-sm">
                            <div className="flex flex-col">
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Base Source</span>
                                <span className="font-semibold text-gray-900 dark:text-white">
                                    Version {selectedOption?.uiVersion ?? toUiVersionNumber(currentVersion ?? 0)}
                                </span>
                                <span className="text-xs text-gray-500 truncate max-w-[140px]">
                                    {selectedOption?.recipeCode || "Draft"}
                                </span>
                            </div>

                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white dark:bg-gray-800 shadow-sm border border-border/50 text-primary">
                                <ArrowRight className="w-4 h-4" />
                            </div>

                            <div className="flex flex-col items-end">
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">New Version</span>
                                <span className="font-semibold text-primary dark:text-purple-300">
                                    Version {nextUiVersionNumber}
                                </span>
                                <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                                    In Development
                                </span>
                            </div>
                        </div>

                        <div className="pt-2 border-t border-primary/10 flex items-start gap-2 text-xs text-gray-600 dark:text-gray-300">
                            <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                            <span>
                                All ingredients, basic parameters, and SOP configurations from <strong>Version {selectedOption?.uiVersion ?? toUiVersionNumber(currentVersion ?? 0)}</strong> will be copied into <strong>Version {nextUiVersionNumber}</strong>.
                            </span>
                        </div>
                    </div>

                    <p className="text-xs text-gray-400 font-normal">
                        Make sure to only perform this function with proper authorization.<span className="text-red-500">*</span>
                    </p>

                    {error && (
                        <div className="w-full p-3 text-xs text-red-600 border border-red-200 rounded-xl bg-red-50 dark:bg-red-950/40 dark:border-red-900/50">
                            {error}
                        </div>
                    )}
                </div>

                <ModalFooter className="flex flex-row justify-between gap-4 mt-4 pt-0">
                    <Button
                        type="button"
                        intent="outline"
                        onClick={() => handleClose(false)}
                        disabled={isLoading}
                        className="flex-1 h-12 text-sm font-bold rounded-xl border-[#D7CEF0] dark:border-primary/40 text-primary dark:text-purple-300 hover:bg-primary-shade-2 bg-transparent"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        intent="primary"
                        onClick={handleConfirm}
                        disabled={isLoading || !selectedBaseRecipeId}
                        className="flex-1 h-12 text-sm font-bold rounded-xl bg-primary hover:bg-primary/90 text-white shadow-md"
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Creating...
                            </>
                        ) : (
                            `Create Version ${nextUiVersionNumber}`
                        )}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
